/**
 * 材料の原料(#216)。マニュアル編集の入力と、本文の値の相互変換。
 *
 * 本文(analysis.json)と編集の記録(manualEdits の value)は数値を持つ JSON。
 * 入力中は「60.」のような途中の文字列を保つため、画面では各欄を文字列で持ち、
 * 保存の直前に normalizeRawMaterials で本文と同じ形へ揃える。
 * 必要量の算出規則は CLI(src/core/raw-materials.ts)と同じ。
 */

export interface RawMaterial {
  name: string;
  quantity: number | null;
  unit: string | null;
  yield_pct: number | null;
  note: string | null;
}

/** 入力中の1行(すべて文字列) */
export interface RawMaterialRow {
  name: string;
  quantity: string;
  unit: string;
  yield_pct: string;
  note: string;
}

export const EMPTY_ROW: RawMaterialRow = { name: "", quantity: "", unit: "", yield_pct: "", note: "" };

function toNumber(s: string): number | null {
  const v = s.trim().replace(/[%％]/g, "");
  if (!v) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** 歩留まりとして受け付ける値か(0 より大きく 100 以下) */
export function validYield(s: string): boolean {
  if (!s.trim()) return true;
  const n = toNumber(s);
  return n != null && n > 0 && n <= 100;
}

/** 分量として受け付ける値か(0 より大きい数値) */
export function validQuantity(s: string): boolean {
  if (!s.trim()) return true;
  const n = toNumber(s);
  return n != null && n > 0;
}

/**
 * 入力中の原料に保存できない値があるか(#238)。歩留まり・量が読めない行を保存すると
 * normalizeRawMaterials が黙って空にするので、保存の前に止める(エラー表示は RawMaterialRows)
 */
export function hasInvalidRawMaterial(value: string): boolean {
  return rowsOf(value).some((r) => !validYield(r.yield_pct) || !validQuantity(r.quantity));
}

/** 本文の値 → 比較・保存用の文字列。原料なしは空文字(編集なしと同じ) */
export function serializeRawMaterials(list: readonly RawMaterial[] | null | undefined): string {
  if (!list || list.length === 0) return "";
  return JSON.stringify(
    list.map((r) => ({
      name: r.name,
      quantity: r.quantity ?? null,
      unit: r.unit ?? null,
      yield_pct: r.yield_pct ?? null,
      note: r.note ?? null,
    })),
  );
}

/** 保存用の文字列(または入力中の文字列)→ 入力行 */
export function rowsOf(value: string): RawMaterialRow[] {
  if (!value.trim()) return [];
  try {
    const raw = JSON.parse(value) as unknown;
    if (!Array.isArray(raw)) return [];
    const s = (x: unknown) => (x == null ? "" : String(x));
    return raw.map((r: Record<string, unknown>) => ({
      name: s(r.name),
      quantity: s(r.quantity),
      unit: s(r.unit),
      yield_pct: s(r.yield_pct),
      note: s(r.note),
    }));
  } catch {
    return [];
  }
}

/** 入力行 → 入力中の文字列(途中の値を保つため文字列のまま入れる) */
export function draftOf(rows: readonly RawMaterialRow[]): string {
  return rows.length ? JSON.stringify(rows) : "";
}

/** 入力中の文字列 → 保存用の文字列。名前の無い行は落とし、読めない数値は空にする */
export function normalizeRawMaterials(value: string): string {
  const list: RawMaterial[] = rowsOf(value)
    .filter((r) => r.name.trim())
    .map((r) => {
      const q = toNumber(r.quantity);
      const y = toNumber(r.yield_pct);
      return {
        name: r.name.trim(),
        quantity: q != null && q > 0 ? q : null,
        unit: r.unit.trim() || null,
        yield_pct: y != null && y > 0 && y <= 100 ? y : null,
        note: r.note.trim() || null,
      };
    });
  return serializeRawMaterials(list);
}

/**
 * 原料の必要量(CLI の rawMaterialAmount と同じ規則)。
 * 書いた量 → 原料が1つだけなら 材料の分量 ÷ 歩留まり(小数第1位へ切り上げ)。
 */
export function rawAmountOf(
  parent: { quantity: number | null; unit: string | null },
  rows: readonly RawMaterialRow[],
  row: RawMaterialRow,
): { quantity: number; unit: string | null; derived: boolean } | null {
  const q = toNumber(row.quantity);
  if (q != null && q > 0) return { quantity: q, unit: row.unit.trim() || null, derived: false };
  if (rows.filter((r) => r.name.trim()).length !== 1) return null;
  const y = toNumber(row.yield_pct);
  if (parent.quantity == null || y == null || y <= 0 || y > 100) return null;
  return { quantity: Math.ceil(((parent.quantity * 100) / y) * 10 - 1e-9) / 10, unit: parent.unit, derived: true };
}
